import produce from 'immer';
import { validateProduct } from './validate-product';
/**
 * Converts a product list returned from the API to our normalized structure
 * When a base product is selected, it will have a value for `options`.
 * When it has variants, it will have one or more keys for `variants`
 * Either variants or options may be null, but not both.
 */
export function normalizeServerList(productList) {
    var _a, _b, _c, _d, _e;
    const items = productList.items;
    const newList = {
        name: productList.name,
        items: {}
    };
    for (const item of items) {
        // skip if product is hidden
        if (!((_a = item === null || item === void 0 ? void 0 : item.product) === null || _a === void 0 ? void 0 : _a.articleNumber)) {
            continue;
        }
        if (item.variant) {
            newList.items[item.product.articleNumber] = {
                options: ((_b = newList.items[item.product.articleNumber]) === null || _b === void 0 ? void 0 : _b.options) || null,
                variants: produce(((_c = newList.items[item.product.articleNumber]) === null || _c === void 0 ? void 0 : _c.variants) || {}, (draft) => {
                    draft[item.variant.articleNumber] = {
                        options: {
                            quantity: item.quantity || 1,
                            description: item.description
                        },
                        parentArticleNumber: item.product.articleNumber
                    };
                })
            };
        }
        else {
            newList.items[item.product.articleNumber] = {
                variants: ((_d = newList.items[item.product.articleNumber]) === null || _d === void 0 ? void 0 : _d.variants) || null,
                options: {
                    quantity: (_e = item.quantity) !== null && _e !== void 0 ? _e : 1,
                    description: item.description
                }
            };
        }
    }
    return newList;
}
/**
 * Takes a flattened product list and result of a products query and returns
 * product details for each item in the flattened list. */
export function getProductDetailForFlattenedProductList(
/** May contain base products, variants, or a combination of both. */
flattenedList, 
/** Contains the results of a products query, which returns only base products */
productQueryResult) {
    const { data } = productQueryResult;
    const productsFromQuery = (data === null || data === void 0 ? void 0 : data.products) || [];
    return flattenedList
        .map((listItem) => {
        const baseProductArticleNumber = listItem.parentArticleNumber || listItem.articleNumber;
        // Find the base product in the query response
        const product = productsFromQuery.find((prod) => prod.articleNumber === baseProductArticleNumber);
        if (!product)
            return null;
        // Get the variant detail from the product
        const selectedVariant = getVariantFromProductDetails(listItem.articleNumber, product);
        const productListProduct = Object.assign(Object.assign({}, product), { quantity: listItem.options.quantity, isVariant: !!listItem.parentArticleNumber, variant: selectedVariant });
        return Object.assign(Object.assign({}, productListProduct), { validation: validateProduct(productListProduct) });
    })
        .filter(Boolean);
}
export function getVariantFromProductDetails(variant, product) {
    if (!variant)
        return null;
    const matchedVariant = product.variants.values.find((value) => value.articleNumber === variant);
    return matchedVariant || null;
}
export function flattenList(list) {
    const flattenedList = [];
    const baseProducts = list ? Object.keys(list.items) : [];
    for (const baseArticleNumber of baseProducts) {
        const variants = list.items[baseArticleNumber].variants;
        if (variants) {
            const variantArticleNumbers = Object.keys(variants);
            for (const articleNumber of variantArticleNumbers) {
                flattenedList.push({
                    articleNumber,
                    parentArticleNumber: baseArticleNumber,
                    options: variants[articleNumber].options
                });
            }
            if (list.items[baseArticleNumber].options) {
                flattenedList.push({
                    articleNumber: baseArticleNumber,
                    options: list.items[baseArticleNumber].options
                });
            }
        }
        else {
            flattenedList.push({
                articleNumber: baseArticleNumber,
                options: list.items[baseArticleNumber].options
            });
        }
    }
    return flattenedList;
}
//# sourceMappingURL=list-transforms.js.map